import React, { useRef, useEffect, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { socket } from '../socket';
import './WebcamStream.css';


const WebcamStream = forwardRef(({ active, onAlertReceived }, ref) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  
  // Expose videoRef to parent components
  useImperativeHandle(ref, () => ({
    video: videoRef.current
  }));
  const [videoInitialized, setVideoInitialized] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('Connecting...');
  const [connected, setConnected] = useState(socket.connected);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [playbackError, setPlaybackError] = useState(null);
  const streamRef = useRef(null); // Keep track of the current stream
  const retryTimeoutRef = useRef(null); // For tracking timeout for retry attempts
  const activePrevRef = useRef(active); // Track changes to active
  // Add a flag to track if video is actually playing despite error
  const isActuallyPlayingRef = useRef(false);
  // Add a reference for the frame capture interval
  const frameIntervalRef = useRef(null);

  // Check initial connection state
  useEffect(() => {
    console.log("Initial socket connection state:", socket.connected);
    setConnected(socket.connected);
  }, []);
  
  // Define the captureFrameForProctoring function
  const captureFrameForProctoring = useCallback(() => {
    if (!videoRef.current || !canvasRef.current || !active) {
      return;
    }
    
    try {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      
      // Check if video dimensions are available
      if (!video.videoWidth || !video.videoHeight) {
        console.log("Video dimensions not available yet, skipping frame capture");
        return;
      }
      
      // Set canvas dimensions to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      // Draw the current video frame to the canvas
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      // Convert canvas to data URL (JPEG format with quality 0.8)
      const imageData = canvas.toDataURL('image/jpeg', 0.8);
      
      // Send the frame to the server for proctoring
      if (socket.connected) {
        socket.emit('video_frame', { image: imageData });
      }
      
      // Set up interval for continuous frame capture if not already set
      if (!frameIntervalRef.current && active) {
        frameIntervalRef.current = setInterval(() => {
          captureFrameForProctoring();
        }, 5000); // Capture every 5 seconds
      }
    } catch (err) {
      console.error("Error capturing frame:", err);
    }
  }, [active, videoRef, canvasRef, socket]);
  
  // Track active prop changes
  useEffect(() => {
    if (active !== activePrevRef.current) {
      console.log(`Active state changed from ${activePrevRef.current} to ${active}`);
      activePrevRef.current = active;
      
      // If becoming active, ensure we have a working camera
      if (active && !videoInitialized) {
        console.log("Exam became active - initializing webcam");
        initializeWebcam();
      }
    }
  }, [active]);

  // Add effect to check if video is actually playing despite error message
  useEffect(() => {
    // Only run this check if there's a playback error but video might actually be playing
    if (playbackError && videoRef.current && !videoRef.current.paused) {
      console.log("Video appears to be playing despite error state");
      
      // This checks if frames are actually being rendered from the camera
      const checkVideoIsActuallyPlaying = () => {
        try {
          if (videoRef.current && videoRef.current.readyState >= 2 && 
              videoRef.current.videoWidth && videoRef.current.videoWidth > 0 && 
              videoRef.current.videoHeight && videoRef.current.videoHeight > 0) {
            console.log("Video has dimensions, indicating it's actually playing");
            isActuallyPlayingRef.current = true;
            
            // If we're successfully processing frames (indicated by active proctoring),
            // clear the error message to avoid confusing the user
            if (active && videoInitialized) {
              console.log("Clearing misleading playback error since video is actually working");
              setPlaybackError(null);
            }
          }
        } catch (err) {
          console.error("Error checking if video is playing:", err);
          // Don't set any error state here, just log it
        }
      };
      
      // Check immediately and after a short delay to allow video to fully initialize
      checkVideoIsActuallyPlaying();
      setTimeout(checkVideoIsActuallyPlaying, 1000);
    }
  }, [playbackError, videoInitialized, active]);

  const stopAllTracks = useCallback(() => {
    clearTimeout(retryTimeoutRef.current);
    
    // Clear frame capture interval if it exists
    if (frameIntervalRef.current) {
      console.log("Clearing frame capture interval");
      clearInterval(frameIntervalRef.current);
      frameIntervalRef.current = null;
    }
    
    // First stop any existing stream tracks
    if (streamRef.current) {
      console.log("Stopping all tracks on current stream");
      try {
        const tracks = streamRef.current.getTracks();
        tracks.forEach(track => {
          console.log(`Stopping track: ${track.kind} - ${track.id} - ${track.readyState}`);
          track.stop();
        });
      } catch (err) {
        console.error("Error stopping tracks:", err);
      }
      streamRef.current = null;
    }
    
    // Also clean up video element srcObject if it exists
    if (videoRef.current && videoRef.current.srcObject) {
      try {
        const tracks = videoRef.current.srcObject.getTracks();
        tracks.forEach(track => {
          console.log(`Stopping video element track: ${track.kind} - ${track.readyState}`);
          track.stop();
        });
      } catch (err) {
        console.error("Error stopping video element tracks:", err);
      }
      videoRef.current.srcObject = null;
    }
  }, []);

  const initializeWebcam = useCallback(async () => {
    console.log("Initializing webcam...");
    try {
      setLoading(true);
      setError(null);
      setPermissionDenied(false);
      setPlaybackError(null);
      isActuallyPlayingRef.current = false;
      
      // Stop any existing tracks first
      stopAllTracks();
      
      console.log("Requesting camera access...");
      setConnectionStatus('Requesting camera permissions...');
      
      // Request camera with explicit constraints
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: 640 },
            height: { ideal: 480 },
            facingMode: 'user'
          },
          audio: false
        });
        
        console.log("Camera stream acquired successfully:", stream);
        console.log("Stream tracks:", stream.getTracks().map(t => `${t.kind}:${t.readyState}:${t.id}`).join(', '));
        
        // Store the stream reference
        streamRef.current = stream;
        
        console.log("Camera access granted");
        setConnectionStatus('Camera permission granted, setting up video...');
        
        // Monitor stream for track-ended events
        stream.getTracks().forEach(track => {
          track.onended = () => {
            console.log(`Track ${track.kind} ended unexpectedly`);
            if (active && videoInitialized) {
              setError("Camera stream ended unexpectedly. Try restarting the camera.");
              setVideoInitialized(false);
            }
          };
        });
        
        // Apply stream to video element
        if (videoRef.current) {
          // Set up event listeners for video element
          videoRef.current.onloadedmetadata = () => {
            console.log("Video metadata loaded");
            try {
              if (videoRef.current) {
                console.log(`Video dimensions: ${videoRef.current.videoWidth || 'unknown'}x${videoRef.current.videoHeight || 'unknown'}`);
              }
              setConnectionStatus('Video metadata loaded, starting playback...');
              
              // Start capturing frames for proctoring once video is playing
              if (active) {
                captureFrameForProctoring();
              }
            } catch (err) {
              console.error("Error in onloadedmetadata handler:", err);
            }
          };
          
          // Make sure we handle timeupdate events which indicate video is playing
          videoRef.current.ontimeupdate = () => {
            // This event fires regularly when the video is playing
            try {
              if (!isActuallyPlayingRef.current && videoRef.current && videoRef.current.videoWidth && videoRef.current.videoWidth > 0) {
                console.log("Video is actually playing (detected via timeupdate)");
                isActuallyPlayingRef.current = true;
                setVideoInitialized(true);
                setPlaybackError(null);
                
                // Start capturing frames if we're in active mode
                if (active) {
                  captureFrameForProctoring();
                }
              }
            } catch (err) {
              console.error("Error in ontimeupdate handler:", err);
              // Don't set error state here to avoid UI disruption
            }
          };
          
          videoRef.current.onerror = (err) => {
            console.error("Video element error:", err);
            setError(`Video element error: ${err.target.error ? err.target.error.message : 'Unknown error'}`);
          };
          
          // Assign the stream to video element
          videoRef.current.srcObject = stream;
          
          // Force play with proper error handling for autoplay restrictions
          try {
            console.log("Attempting to play video...");
            const playPromise = videoRef.current.play();
            
            if (playPromise !== undefined) {
              playPromise.then(() => {
                console.log("Video playback started successfully");
                setLoading(false);
                setVideoInitialized(true);
                setPlaybackError(null); // Clear any playback errors
                isActuallyPlayingRef.current = true;
                setConnectionStatus(connected ? 'Connected to server' : 'Video ready, waiting for server connection');
              }).catch(err => {
                console.error("Error starting video playback:", err);
                
                // Check if video is actually playing despite the error
                if (videoRef.current && !videoRef.current.paused && 
                    videoRef.current.videoWidth && videoRef.current.videoWidth > 0 && 
                    videoRef.current.videoHeight && videoRef.current.videoHeight > 0) {
                  console.log("Video appears to be playing despite play() promise rejection");
                  setLoading(false);
                  setVideoInitialized(true);
                  isActuallyPlayingRef.current = true;
                  setPlaybackError(null);
                } else {
                  // This is likely due to browser autoplay policy restrictions
                  setPlaybackError("Browser prevented automatic video playback. Please click 'Retry Webcam' to start the camera.");
                  setError(`Error playing video: ${err.message}`);
                  setLoading(false);
                }
              });
            } else {
              // For older browsers that don't return a promise from play()
              setTimeout(() => {
                if (videoRef.current) {
                  // Check if video actually has frames and is really playing
                  if (videoRef.current.paused) {
                    setPlaybackError("Browser prevented automatic video playback. Please click 'Retry Webcam' to start the camera.");
                    setError("Video is paused. Click 'Retry Webcam' to manually start playback.");
                  } else if (videoRef.current.videoWidth && videoRef.current.videoWidth > 0 && 
                             videoRef.current.videoHeight && videoRef.current.videoHeight > 0) {
                    setVideoInitialized(true);
                    setPlaybackError(null); // Clear any playback errors
                    isActuallyPlayingRef.current = true;
                  }
                  setLoading(false);
                }
              }, 1000);
            }
          } catch (err) {
            console.error("Error starting video playback:", err);
            
            // Even if play() throws, check if the video is actually playing
            setTimeout(() => {
              if (videoRef.current && !videoRef.current.paused &&
                  videoRef.current.videoWidth && videoRef.current.videoWidth > 0 && 
                  videoRef.current.videoHeight && videoRef.current.videoHeight > 0) {
                console.log("Video is playing despite play() error");
                setVideoInitialized(true);
                isActuallyPlayingRef.current = true;
                setPlaybackError(null);
              } else {
                setError(`Error playing video: ${err.message}`);
                setPlaybackError("Browser prevented automatic video playback. Please click 'Retry Webcam' to start the camera.");
              }
              setLoading(false);
            }, 1000);
          }
        } else {
          console.error("Video element reference lost during initialization");
          setError("Video element not found. Please refresh the page.");
          setLoading(false);
        }
      } catch (err) {
        console.error("Camera access error:", err);
        if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
          setPermissionDenied(true);
          setError("Camera access denied. Please grant camera permissions to continue.");
        } else if (err.name === "NotFoundError" || err.name === "DevicesNotFoundError") {
          setError("No camera detected. Please connect a camera to continue.");
        } else if (err.name === "NotReadableError" || err.name === "TrackStartError") {
          setError("Camera is in use by another application. Please close other applications that might be using your camera.");
          // Try again after a delay
          if (active) {
            retryTimeoutRef.current = setTimeout(() => {
              console.log("Retrying camera access after error...");
              initializeWebcam();
            }, 2000);
          }
        } else {
          setError(`Camera error: ${err.message || "Unknown camera error"}`);
        }
        setLoading(false);
      }
    } catch (err) {
      console.error("Error initializing webcam:", err);
      setError(`Initialization error: ${err.message}`);
      setLoading(false);
    }
  }, [connected, stopAllTracks, active]);

  // This effect specifically watches for changes to the 'active' prop
  // and ensures webcam is initialized when exam starts
  useEffect(() => {
    if (active && !videoInitialized) {
      console.log("Exam activated - initializing webcam");
      initializeWebcam();
    }
    
    // Check if we need to clean up when becoming inactive
    if (!active && videoInitialized) {
      console.log("Exam deactivated - cleaning up webcam resources");
      // Stop all streams when exam is inactive
      stopAllTracks();
      setVideoInitialized(false);
      
      // Cancel any pending retries
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    }
  }, [active, videoInitialized, initializeWebcam, stopAllTracks]);

  const restartWebcam = async () => {
    // This function will be called on user interaction, which should satisfy autoplay policy
    try {
      console.log("Manual webcam restart initiated by user interaction");
      
      // Stop all existing tracks first
      stopAllTracks();
      
      // Reset states before reinitializing
      setVideoInitialized(false);
      setError(null);
      setPlaybackError(null);
      
      // Reinitialize the camera stream with a small delay to ensure cleanup is complete
      setTimeout(async () => {
        await initializeWebcam();
        
        // Force play with user interaction
        if (videoRef.current && videoRef.current.paused) {
          console.log("Attempting to play video after manual restart");
          try {
            await videoRef.current.play();
            console.log("Video play succeeded after manual restart");
            setVideoInitialized(true);
            setPlaybackError(null);
          } catch (err) {
            console.error("Still unable to play video after manual restart:", err);
            setPlaybackError("Still unable to play video. Try refreshing the page.");
          }
        }
      }, 500); // Increased delay to give browser more time
    } catch (err) {
      console.error("Error in manual webcam restart:", err);
      setError(`Restart error: ${err.message}`);
    }
  };

  const retryWebcam = () => {
    console.log("Retry webcam requested");
    restartWebcam();
  };

  // Setup socket event listeners
  useEffect(() => {
    console.log("Setting up socket listeners");
    
    // Force a reconnection attempt if not connected
    if (!socket.connected) {
      console.log("Socket not connected, attempting to connect...");
      socket.connect();
    }

    const handleConnect = () => {
      console.log('Socket connected:', socket.id);
      setConnected(true);
      setConnectionStatus('Connected to server');
    };

    const handleDisconnect = (reason) => {
      console.log('Socket disconnected:', reason);
      setConnected(false);
      setConnectionStatus(`Disconnected: ${reason}`);
    };

    const handleConnectError = (err) => {
      console.error('Socket connection error:', err);
      setConnected(false);
      setConnectionStatus(`Connection error: ${err.message}`);
    };

    const handleAlerts = (data) => {
      console.log('🎥 WebcamStream received alerts:', data);
      if (data.alerts && data.alerts.length > 0) {
        data.alerts.forEach(alert => {
          // Check if the alert is an object with type and message
          if (typeof alert === 'object' && alert.type && alert.message) {
            onAlertReceived({
              message: alert.message,
              timestamp: alert.timestamp || new Date().toISOString(),
              type: alert.type,
              warning_count: alert.warning_count,
              max_warnings: alert.max_warnings
            });
          } else {
            // For backward compatibility, handle string alerts
            // Try to determine the alert type from the message content
            let alertType = 'warning';
            
            if (alert.includes('eye movement') || alert.includes('gaze')) {
              alertType = 'GAZE';
            } else if (alert.includes('mouth') || alert.includes('talking') || alert.includes('speech')) {
              alertType = 'SPEECH';
            } else if (alert.includes('phone')) {
              alertType = 'PHONE';
            } else if (alert.includes('person') || alert.includes('face')) {
              alertType = 'PERSON';
            }
            
            onAlertReceived({
              message: alert,
              timestamp: new Date().toISOString(),
              type: alertType
            });
          }
        });
      }
    };

    // Register event handlers
    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('connect_error', handleConnectError);
    // Note: Alert handling is now done by IntegratedExamInterface to prevent conflicts
    // socket.on('proctoring_alerts', handleAlerts);
    // socket.on('critical_alert', (data) => {
    //   console.log('🎥 Critical alert received in WebcamStream:', data);
    //   // Pass the critical alert to the parent component
    //   onAlertReceived({
    //     message: data.message,
    //     timestamp: data.timestamp || new Date().toISOString(),
    //     type: data.type || 'error',
    //     warning_count: data.warning_count,
    //     max_warnings: data.max_warnings
    //   });
    // });
    socket.on('connection_response', (data) => {
      console.log('Connection response:', data);
      if (data.status === 'connected') {
        setConnected(true);
        setConnectionStatus('Connected to server');
      }
    });

    // Check if already connected
    if (socket.connected) {
      console.log("Socket already connected:", socket.id);
      setConnected(true);
      setConnectionStatus('Connected to server');
    }
    
    return () => {
      // Clean up event listeners
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('connect_error', handleConnectError);
      // Note: Alert listeners are handled by IntegratedExamInterface
      // socket.off('proctoring_alerts', handleAlerts);
      // socket.off('critical_alert');
      socket.off('connection_response');
    };
  }, [onAlertReceived]);

  // Start/stop video processing based on active prop
  useEffect(() => {
    let processingInterval;
    
    const processFrame = () => {
      if (!videoRef.current || !canvasRef.current || !socket.connected || !active || !videoInitialized) {
        console.log("Cannot process frame - prerequisites not met:", {
          videoRef: !!videoRef.current,
          canvasRef: !!canvasRef.current,
          socketConnected: socket.connected,
          active,
          videoInitialized
        });
        return;
      }
      
      try {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        
        if (video.readyState !== 4) {
          console.log("Video not ready yet, readyState:", video.readyState);
          return;
        }
        
        const context = canvas.getContext('2d');
        
        if (video.videoWidth && video.videoHeight) {
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
        }
        
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        try {
          const imageData = canvas.toDataURL('image/jpeg', 0.5);
          socket.emit('video_frame', { image: imageData });
        } catch (err) {
          console.error("Error converting canvas to image:", err);
        }
      } catch (err) {
        console.error("Error processing video frame:", err);
      }
    };
    
    if (active && videoInitialized && socket.connected) {
      console.log('Starting video processing, socket.connected:', socket.connected);

      // Don't emit start_proctoring here - let parent component handle it to prevent duplicates
      // socket.emit('start_proctoring');

      processingInterval = setInterval(processFrame, 200);
    } else if (!active && socket.connected) {
      console.log('Stopping video processing');

      // Don't emit stop_proctoring here - let parent component handle it to prevent duplicates
      // socket.emit('stop_proctoring');

      if (processingInterval) {
        clearInterval(processingInterval);
      }
    }
    
    return () => {
      if (processingInterval) {
        clearInterval(processingInterval);
      }
    };
  }, [active, videoInitialized, connected]);

  // Monitor video element for unexpected errors/pauses
  useEffect(() => {
    if (!videoRef.current) return;
    
    const videoEl = videoRef.current;
    
    const handlePause = () => {
      console.log("Video paused event");
      // Only care about pauses when we should be playing
      if (active && videoInitialized && !playbackError) {
        console.log("Unexpected video pause detected");
        
        // Check if video might still be working despite pause event
        setTimeout(() => {
          if (videoRef.current && !videoRef.current.paused &&
              videoRef.current.videoWidth > 0 && videoRef.current.videoHeight > 0) {
            console.log("False positive pause event - video is actually playing");
            isActuallyPlayingRef.current = true;
          } else {
            setPlaybackError("Video playback was interrupted. Click 'Retry Webcam' to restart.");
            setVideoInitialized(false);
          }
        }, 300); // Short delay to see if it's a momentary pause
      }
    };
    
    const handleStalled = () => {
      console.log("Video stalled event");
      if (active && videoInitialized) {
        // Don't immediately show error for stalls, check if video recovers
        setTimeout(() => {
          if (videoRef.current && !videoRef.current.paused &&
              videoRef.current.videoWidth > 0 && videoRef.current.videoHeight > 0) {
            console.log("Video recovered from stall automatically");
            isActuallyPlayingRef.current = true;
          } else {
            setError("Video stream stalled. Network issues may be affecting the camera.");
          }
        }, 1000);
      }
    };
    
    const handleEnded = () => {
      console.log("Video ended event");
      if (active) {
        // Similar check before showing error
        setTimeout(() => {
          if (videoRef.current && !videoRef.current.paused &&
              videoRef.current.videoWidth > 0 && videoRef.current.videoHeight > 0) {
            console.log("False positive ended event - video is actually playing");
            isActuallyPlayingRef.current = true;
          } else {
            setVideoInitialized(false);
            setError("Video stream ended unexpectedly.");
          }
        }, 500);
      }
    };
    
    // Add a periodic check to make sure we clear any incorrect error overlays
    // if video is actually playing
    let periodicCheckInterval;
    if (active) {
      periodicCheckInterval = setInterval(() => {
        // If we have an error but video is playing, clear the error
        if (videoRef.current) {
          // First check if video is actually displaying content
          const isVideoWorking = !videoRef.current.paused && 
                                videoRef.current.videoWidth > 0 && 
                                videoRef.current.videoHeight > 0;
          
          // Handle the case where we have an error overlay but video is working
          if ((playbackError || error) && isVideoWorking) {
            console.log("Periodic check found working video despite error state");
            isActuallyPlayingRef.current = true;
            
            // We can safely clear the playback error if the video is showing frames
            if (playbackError) {
              console.log("Clearing misleading playback error");
              setPlaybackError(null);
            }
            
            // For other errors, only clear if it's the "play interrupted" style message
            if (error && (error.includes("play() request was interrupted") || 
                          error.includes("play interrupted") ||
                          error.includes("Error playing video"))) {
              console.log("Clearing misleading error message");
              setError(null);
            }
          }
          
          // Handle case where video stopped playing for some reason but should be
          if (videoInitialized && !isVideoWorking && active) {
            console.log("Video appears to have stopped playing but should be active");
            
            // Try to restart the video without a full reinitialize
            if (videoRef.current.srcObject && videoRef.current.srcObject.active) {
              console.log("Attempting to restart video playback without reinitializing stream");
              videoRef.current.play().catch(err => {
                console.error("Could not restart video playback:", err);
                // If simple play() doesn't work, we may need a full restart
                if (!isActuallyPlayingRef.current) {
                  console.log("Video still not playing, trying full restart");
                  restartWebcam();
                }
              });
            } else if (!videoRef.current.srcObject || !videoRef.current.srcObject.active) {
              // Stream is missing or inactive, need to reinitialize
              console.log("Video stream is missing or inactive, reinitializing webcam");
              restartWebcam();
            }
          }
        }
      }, 2000); // Check every 2 seconds
    }
    
    // Add event listeners
    videoEl.addEventListener('pause', handlePause);
    videoEl.addEventListener('stalled', handleStalled);
    videoEl.addEventListener('ended', handleEnded);
    
    return () => {
      // Remove event listeners
      videoEl.removeEventListener('pause', handlePause);
      videoEl.removeEventListener('stalled', handleStalled);
      videoEl.removeEventListener('ended', handleEnded);
      
      // Clear interval
      if (periodicCheckInterval) {
        clearInterval(periodicCheckInterval);
      }
    };
  }, [active, videoInitialized, playbackError, error]);



  // Add debug code for the video reference
  const debugVideoStatus = () => {
    if (!videoRef.current) {
      console.log("Video element not found");
      return "Video element not found";
    }

    const video = videoRef.current;
    const streamInfo = video.srcObject ? {
      active: video.srcObject.active,
      id: video.srcObject.id,
      tracks: video.srcObject.getTracks().map(t => ({
        kind: t.kind,
        ready: t.readyState,
        enabled: t.enabled,
        id: t.id
      }))
    } : "No stream";
    
    console.log("Video element info:", {
      readyState: video.readyState,
      paused: video.paused,
      currentSrc: video.currentSrc,
      srcObject: streamInfo,
      videoWidth: video.videoWidth,
      videoHeight: video.videoHeight,
      clientWidth: video.clientWidth,
      clientHeight: video.clientHeight,
      isActuallyPlaying: isActuallyPlayingRef.current,
      hasPlaybackError: !!playbackError,
      hasError: !!error,
      errorMessage: error,
      playbackErrorMessage: playbackError
    });

    // Try to force play the video if needed
    if (video.paused) {
      video.play().then(() => {
        console.log("Video play forced successfully");
        setVideoInitialized(true);
        setPlaybackError(null);
        isActuallyPlayingRef.current = true;
      }).catch(err => {
        console.error("Error forcing video play:", err);
        
        // Check if video is actually playing despite the error
        if (!video.paused && video.videoWidth > 0 && video.videoHeight > 0) {
          console.log("Video appears to be playing despite play() error");
          isActuallyPlayingRef.current = true;
          setPlaybackError(null);
        } else {
          setPlaybackError("Unable to play video: " + err.message);
        }
      });
    } else if (video.videoWidth > 0 && video.videoHeight > 0) {
      // If video is playing and showing frames, update our flag
      isActuallyPlayingRef.current = true;
      
      // Also clear any misleading errors
      if (playbackError || (error && error.includes("play()"))) {
        console.log("Clearing misleading errors since video is playing");
        setPlaybackError(null);
        if (error && error.includes("play()")) {
          setError(null);
        }
      }
    }

    return "Check console for video details. Video actually playing: " + 
           (isActuallyPlayingRef.current ? "YES" : "NO") + 
           (playbackError ? " (but has playback error)" : "");
  };

  // Clean up function to stop all video tracks when component unmounts
  useEffect(() => {
    return () => {
      console.log("WebcamStream unmounting - cleaning up tracks");
      clearTimeout(retryTimeoutRef.current);
      stopAllTracks();
    };
  }, [stopAllTracks]);

  // Add useEffect to observe and maintain video playing state
  useEffect(() => {
    if (!videoRef.current) return;
    
    // Function to check video state and update isActuallyPlayingRef
    const checkVideoState = () => {
      const video = videoRef.current;
      if (!video) return;
      
      const wasPlaying = isActuallyPlayingRef.current;
      const isPlaying = !video.paused && video.videoWidth > 0 && video.videoHeight > 0;
      
      // Update our ref if state has changed
      if (isPlaying !== wasPlaying) {
        console.log(`Video playing state changed from ${wasPlaying} to ${isPlaying}`);
        isActuallyPlayingRef.current = isPlaying;
        
        // If we're now playing and had an error, clear it
        if (isPlaying && (playbackError || (error && error.includes("play()")))) {
          console.log("Clearing errors since video is now playing");
          setPlaybackError(null);
          if (error && error.includes("play()")) {
            setError(null);
          }
        }
        
        // If we were playing but now we're not, attempt to restart playback
        if (wasPlaying && !isPlaying && active) {
          console.log("Video was playing but stopped - attempting to restart");
          if (video.srcObject && video.srcObject.active) {
            console.log("Stream is still active, just trying to play again");
            video.play().catch(err => {
              console.error("Failed to restart playback:", err);
            });
          }
        }
      }
    };
    
    // Check video state immediately
    checkVideoState();
    
    // Set up events to track video state
    const videoEvents = ['playing', 'pause', 'timeupdate', 'canplay', 'loadeddata'];
    videoEvents.forEach(eventName => {
      videoRef.current.addEventListener(eventName, checkVideoState);
    });
    
    // Set up periodic check to maintain video state
    const videoCheckInterval = setInterval(checkVideoState, 1000);
    
    return () => {
      // Clean up all event listeners
      if (videoRef.current) {
        videoEvents.forEach(eventName => {
          videoRef.current.removeEventListener(eventName, checkVideoState);
        });
      }
      clearInterval(videoCheckInterval);
    };
  }, [videoRef.current, active, playbackError, error]);

  return (
    <Box sx={{ textAlign: 'center' }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {active ? 'Proctoring Active' : 'Proctoring Inactive'}
      </Typography>
      
      <div className={`webcam-container ${playbackError || error ? 'has-error' : ''} ${isActuallyPlayingRef.current ? 'video-playing' : ''}`}>
        {/* Always render the video element with the same props regardless of error state */}
        <video 
          ref={videoRef}
          className="webcam-video"
          playsInline
          autoPlay
          muted
          style={{ display: 'block', width: '100%' }}
        />
        <canvas 
          ref={canvasRef} 
          style={{ display: 'none' }} 
        />
        
        {/* Conditionally render the error overlay on top */}
        {(playbackError && !isActuallyPlayingRef.current) && (
          <div className="error-overlay">
            <p>Error playing video:</p>
            <p>{playbackError}</p>
            <p>Try refreshing the page or check your camera permissions.</p>
            <Button variant="contained" color="primary" onClick={retryWebcam}>
              RETRY WEBCAM
            </Button>
          </div>
        )}
        
        {(error && !isActuallyPlayingRef.current && !playbackError) && (
          <div className="error-overlay">
            <p>Error: {error}</p>
            <p>Try refreshing the page or check your camera permissions.</p>
            <Button variant="contained" color="primary" onClick={retryWebcam}>
              Retry Webcam
            </Button>
          </div>
        )}
        
        {loading && (
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
            <p className="loading-text">{connectionStatus}</p>
            {error && <p className="error-text">{error}</p>}
          </div>
        )}
        
        <Typography variant="caption" sx={{ position: 'absolute', bottom: 2, left: 2, backgroundColor: 'rgba(0,0,0,0.5)', color: 'white', padding: '2px 4px' }}>
          Video initialized: {videoInitialized ? 'Yes' : 'No'} | Playing: {isActuallyPlayingRef.current ? 'Yes' : 'No'}
        </Typography>
      </div>
      
      <Typography variant="caption" color={connected ? "success.main" : "error.main"} sx={{ display: 'block', mt: 1 }}>
        {connected ? 'Connected to server' : 'Disconnected from server'}
      </Typography>
      

      
      <Button 
        variant="contained" 
        color="primary" 
        size="small"
        sx={{ mt: 1 }}
        onClick={() => {
          try {
            // Check if video is already playing correctly before restarting
            if (videoRef.current && !videoRef.current.paused && 
                videoRef.current.videoWidth && videoRef.current.videoWidth > 0 && 
                videoRef.current.videoHeight && videoRef.current.videoHeight > 0) {
              console.log("Video is already playing correctly - just clearing error states");
              isActuallyPlayingRef.current = true;
              setVideoInitialized(true);
              setPlaybackError(null);
              
              // Only clear specific errors that might be false positives
              if (error && (error.includes("play() request was interrupted") || 
                             error.includes("play interrupted") ||
                             error.includes("Error playing video"))) {
                setError(null);
              }
            } else {
              // Actually restart the camera if it's not working
              restartWebcam();
            }
          } catch (err) {
            console.error("Error in restart button handler:", err);
            // If we get an error trying to check video status, just restart it
            restartWebcam();
          }
        }}
      >
        {(playbackError || error) ? "Clear Error & Restart Camera" : "Restart Camera"}
      </Button>
      
      {!connected && (
        <Button 
          variant="outlined" 
          color="primary" 
          size="small"
          sx={{ mt: 1, ml: 1 }}
          onClick={() => {
            socket.connect();
            console.log("Manual reconnection attempt, socket:", socket);
          }}
        >
          Reconnect
        </Button>
      )}
      
      <Button 
        variant="text" 
        color="secondary" 
        size="small"
        sx={{ mt: 1, ml: 1 }}
        onClick={() => {
          // Check video status and also try playing if needed
          if (videoRef.current && videoRef.current.paused) {
            videoRef.current.play().catch(e => console.error("Error playing video from debug:", e));
          }
          alert(debugVideoStatus());
        }}
      >
        Debug Video
      </Button>
    </Box>
  );
});

export default WebcamStream;